import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Waves, 
  UtensilsCrossed, 
  Mountain, 
  Wifi, 
  Car, 
  Dog,
  Wind,
  Bed,
  Bath,
  Sun,
  TreePine,
  Shield,
  Clock,
  Users,
  ArrowRight,
  Check
} from 'lucide-react';
import { Button } from '../components/ui/button';

const VILLA_HERO = "/images/villa/01.jpg";
const POOL_IMAGE = "/images/villa/02.jpg";
const BEDROOM_IMAGE = "/images/villa/03.jpg";
const HACIENDA_IMAGE = "/images/villa/04.jpg";
const VILLA_01 = "/images/villa/09.jpg";
const VILLA_02 = "/images/villa/01.jpg";
const VILLA_03 = "/images/villa/02.jpg";
const VILLA_04 = "/images/villa/03.jpg";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const amenities = [
  { icon: Waves, title: 'Alberca y Jacuzzi Climatizados', desc: 'Disfruta de nuestra alberca y Jacuzzi climatizados a cualquier hora del día.' },
  { icon: UtensilsCrossed, title: 'Cocina Equipada', desc: 'Todo lo necesario para preparar tus comidas favoritas' },
  { icon: Mountain, title: 'Terraza Panorámica', desc: 'Disfruta de atardeceres inolvidables desde la terraza' },
  { icon: Wifi, title: 'WiFi Alta Velocidad', desc: 'Mantente conectado con internet de alta velocidad' },
  { icon: Car, title: 'Estacionamiento', desc: 'Amplio estacionamiento privado y seguro' },
  { icon: Dog, title: 'Pet Friendly', desc: 'Trae a tu mascota y disfruten juntos (cargo adicional)' },
  { icon: Wind, title: 'Aire Acondicionado', desc: 'Para una estancia más cómoda.' },
  { icon: Sun, title: 'Área de Asador', desc: 'Parrilla equipada para tus reuniones' },
  { icon: TreePine, title: '1,050 m² para Disfrutar', desc: 'Jardines y espacios al aire libre pensados para descansar, convivir y disfrutar de la naturaleza.' },
  { icon: Shield, title: 'Acceso Seguro', desc: '' },
];

const rooms = [
  { icon: Bed, label: '3 Recámaras', desc: 'Camas king y queen size' },
  { icon: Bath, label: '2 Baños Completos', desc: 'Con amenidades de lujo' },
  { icon: Users, label: 'Hasta 10 Huéspedes', desc: 'Espacio para toda la familia' },
];

const rules = [
  'Check-in: 2:00 p. m.',
  'Check-out: 11:00 a. m.',
  'Capacidad máxima: 10 huéspedes.',
  'El hospedaje está destinado al descanso y convivencia; no se permiten fiestas durante la estancia.',
  'No está permitido fumar dentro de la villa. La propiedad cuenta con un área designada para fumadores.',
  'Mascotas: permitidas únicamente con autorización previa y sujetas al reglamento de La Finca.',
];

export default function VillaPage() {
  return (
    <div data-testid="villa-page" className="pt-20">
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${VILLA_HERO})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/20" />
        </div>

        <div className="relative h-full flex flex-col justify-end pb-16 px-4">
          <div className="max-w-7xl mx-auto w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
                Hospedaje
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
                Villa de Descanso
              </h1>
              <p className="text-white/80 text-lg max-w-2xl">
                Un espacio privado pensado para descansar, desconectarte y disfrutar. 1,050 m² rodeados de naturaleza, comodidad y tranquilidad para hacer de tu estancia una experiencia especial.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-[#5C4033] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '10', label: 'Huéspedes' },
              { value: '3', label: 'Recámaras' },
              { value: '3', label: 'Baños' },
              { value: '1,050', label: 'm² de terreno' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-serif text-3xl md:text-4xl text-white">{stat.value}</p>
                <p className="text-white/70 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
                Una Escapada para Disfrutar sin Prisas
              </h2>
              <p className="text-[#1A1A1A]/70 text-lg leading-relaxed mb-6">
                Entre jardines y espacios pensados para el descanso, La Finca es un refugio privado en San Cayetano, Nayarit, donde cada rincón invita a desconectarte de la rutina.
              </p>
              <p className="text-[#1A1A1A]/70 text-lg leading-relaxed mb-8">
                Villa, alberca, jacuzzi, terraza y asador se integran en un mismo lugar para que disfrutes desde una tarde tranquila hasta una estancia especial con familia o amigos.
              </p>
              
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="space-y-4">
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={VILLA_01}
                    alt="Pool"
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={VILLA_02}
                    alt="Exterior"
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              <div className="pt-8">
                <div className="overflow-hidden rounded-2xl">
                  <img
                    src={VILLA_03}
                    alt="Bedroom"
                    className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
              Todo lo que necesitas
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A]">
              Amenidades
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {amenities.map((amenity, index) => (
              <motion.div
                key={amenity.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-[#FAF7F2] rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
              >
                <amenity.icon className="h-8 w-8 text-[#C8962D] mx-auto mb-3" />
                <h3 className="font-medium text-[#1A1A1A] mb-1">{amenity.title}</h3>
                <p className="text-xs text-[#1A1A1A]/60">{amenity.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modalities & Rules */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Modalities */}
            <div>
              <h2 className="font-serif text-3xl text-[#1A1A1A] mb-8">
                Modalidades de Renta
              </h2>
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center mb-3">
                    <Clock className="h-6 w-6 text-[#C8962D] mr-3" />
                    <h3 className="font-serif text-xl text-[#1A1A1A]">Por Noche</h3>
                  </div>
                  <p className="text-[#1A1A1A]/70">
                    Estancia mínima:<br></br>
                    Entre semana: 1 noche<br></br>
                    Sábados: mínimo 2 noches<br></br><br></br>

                    Horarios:<br></br>
                    Check-in: 2:00 p. m.<br></br>
                    Check-out: 11:00 a. m.
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center mb-3">
                    <Sun className="h-6 w-6 text-[#C8962D] mr-3" />
                    <h3 className="font-serif text-xl text-[#1A1A1A]">Day Pass</h3>
                  </div>
                  <p className="text-[#1A1A1A]/70">
                    Disfruta de las áreas exteriores de La Finca en un horario de 2:00 p. m. a 8:00 p. m.<br></br><br></br>
                  </p>
                  <p className="text-[#1A1A1A]/70">
                    Ideal para pasar un día de alberca, relajarte o disfrutar una reunión con familia y amigos.
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center mb-3">
                    <Users className="h-6 w-6 text-[#C8962D] mr-3" />
                    <h3 className="font-serif text-xl text-[#1A1A1A]">Estancia Extendida</h3>
                  </div>
                  <p className="text-[#1A1A1A]/70">
                    Extiende tu experiencia en La Finca y disfruta de la propiedad hasta las 5:00 p. m. el día de tu salida. <br></br><br></br>
                    Disponible como complemento para reservaciones de hospedaje y eventos que incluyan hospedaje, sujeto a disponibilidad.
                  </p>
                </div>
              </div>
            </div>

            {/* House Rules */}
            <div>
              <h2 className="font-serif text-3xl text-[#1A1A1A] mb-8">
                Reglas de Hospedaje
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <ul className="space-y-4">
                  {rules.map((rule) => (
                    <li key={rule} className="flex items-start">
                      <Check className="h-5 w-5 text-[#3D5A3E] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-[#1A1A1A]/70">{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#5C4033]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
            ¿Listo para tu escapada?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Reserva ahora y vive la experiencia La Finca
          </p>
          <Link to="/reservar">
            <Button
              data-testid="villa-cta-reserve-btn"
              size="lg"
              className="bg-[#C8962D] hover:bg-[#B8861D] text-white px-12"
            >
              Reservar Ahora
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
